import { useEffect, useRef, useState } from "react";
import { useAssignmentDraft } from "@/components/assignments/use-assignment-draft";
import { DEFAULT_DELIVERABLE } from "@/components/assignments/work/work-facts";
import type { MaterialSourcePayload } from "@/components/courses/materials/material-facts";
import { SourcePicker } from "@/components/courses/materials/SourcePicker";
import type { MaterialLibrary } from "@/components/courses/materials/use-material-library";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RegisterRow } from "@/components/ui/register";
import { assignmentsApi } from "@/lib/api/assignments";

/** An in-place draft creator. Publishing remains the task's reviewed act. */
export function SessionNewRecord({ kind, library, unitId, onClose, onCreated }: {
  kind: "reading" | "task"; library: MaterialLibrary; unitId: string;
  onClose: (created?: boolean) => void; onCreated: (id?: string) => void;
}) {
  const [title, setTitle] = useState("");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [unknown, setUnknown] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  useAssignmentDraft(!!title.trim() || !!link.trim(), busy);
  useEffect(() => field.current?.focus(), []);
  const create = async (picked?: MaterialSourcePayload) => {
    if (busy || unknown) return;
    setMessage("");
    setBusy(true);
    let createdId: string | undefined;
    try {
      if (kind === "reading" && picked) {
        await library.create({ ...picked, unit_id: unitId });
      } else {
        if (!title.trim() || (kind === "reading" && !/^https?:\/\/\S+$/i.test(link.trim()))) {
          setMessage(kind === "reading" ? "Give the reading a title and a link that starts with https://." : "Give the task a title.");
          return;
        }
        if (kind === "reading") await library.create({ title: title.trim(), file_url: link.trim(), unit_id: unitId });
        else createdId = (await assignmentsApi.create(library.courseId, { title: title.trim(), expected_deliverable: DEFAULT_DELIVERABLE, unit_id: unitId })).id;
      }
      setTitle(""); setLink(""); onCreated(createdId); onClose(true);
    } catch {
      setUnknown(true);
      setMessage("The new record was not confirmed. Close this editor and check the list before adding it again.");
      onCreated();
    } finally { setBusy(false); }
  };
  const id = `new-session-${kind}`;
  return <RegisterRow className="grid-cols-1" detail={<div className="grid gap-3" onKeyDown={event => { if (event.key === "Escape" && !busy) { event.preventDefault(); onClose(); } }}>
    <Label htmlFor={`${id}-title`}>Title</Label><Input ref={field} id={`${id}-title`} value={title} disabled={busy || unknown} onChange={event => setTitle(event.target.value)} />
    {kind === "reading" && <><SourcePicker courseId={library.courseId} title={title} link={link} onLinkChange={setLink} onPick={payload => void create(payload)} disabled={busy || unknown} />
      <p className="text-meta text-muted-foreground">Google Drive or the link&apos;s host decides who can open the file.</p></>}
    <p role="status" className="text-meta">{message}</p>
    <div className="flex flex-wrap gap-2"><Button variant="outline" disabled={busy || unknown} onClick={() => void create()}>{busy ? "Adding..." : `Add ${kind} as a draft`}</Button><Button variant="subtle" disabled={busy} onClick={() => onClose()}>{unknown ? "Check the list" : "Discard"}</Button></div>
  </div>}><h4 className="text-ui font-semibold">New {kind}</h4></RegisterRow>;
}
